<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { ExternalLink, Loader2, TriangleAlert } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { configure, useForm } from 'vee-validate'
import { computed, defineEmits, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as z from 'zod'
import { useFormValidationSchemas } from '@/composables/src/useFormValidationSchemas'
import { Alert, AlertDescription } from '@/foundation/alert'
import { AutoForm } from '@/foundation/auto-form'
import AutoFormField from '@/foundation/auto-form/AutoFormField.vue'
import AutoFormFieldAutocomplete from '@/foundation/auto-form/AutoFormFieldAutocomplete.vue'
import PhoneInput from '@/foundation/auto-form/AutoFormPhoneNumber.vue'
import { Button } from '@/foundation/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/foundation/dialog'
import { createCompanies, updateCompanies } from '@/service/companies'
import { fetchJiraProjects } from '@/service/jira'
import { getAllProducts } from '@/service/products'
import { getAllServices } from '@/service/services'
import { getTogglProjects, getTogglWorkspaces } from '@/service/toggl'
import { useAccountStore } from '@/stores/account'
import { useCompanyStore } from '@/stores/companies'
import { useTeamStore } from '@/stores/teams'
import type { Company } from '@/views/companies/data/schema'
import DialogCheck from '../components/DialogCheck.vue'
import DomainTags from './DomainTags.vue'
import TierIndustryGroup from './TierIndustryGroup.vue'

type Props = {
  customer?: Company | null
}

const props = defineProps<Props>()

configure({
  bails: false,
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: false,
  validateOnModelUpdate: true,
})

const emit = defineEmits(['closeDialog'])

const router = useRouter()

const isEditMode = computed(() => !!props.customer)

const accountStore = useAccountStore()
const { togglToken } = storeToRefs(accountStore)
const companyStore = useCompanyStore()
const teamStore = useTeamStore()
const { teams } = storeToRefs(teamStore)
const { prefixSchema, addressSchema, phoneSchema, domainsSchema } = useFormValidationSchemas()

const isLoading = ref(false)
const isSubmitting = ref(false)
const confirmDuplicate = ref(false)
const showDuplicateDialog = ref(false)
const pendingValues = ref<Record<string, any> | null>(null)
const backendError = ref('')
const duplicateDomains = ref<string[]>([])
const productOptions = ref<{ name: string; id: string }[]>([])
const serviceOptions = ref<{ name: string; id: string }[]>([])
const jiraProjects = ref<{ name: string; id: string }[]>([])
const isLoadingJiraProjects = ref(false)
const togglProjects = ref<any[]>([])
const togglWorkspaceId = ref<number | null>(null)
const isLoadingTogglProjects = ref(false)

const normalize = (str: string | undefined): string => str?.trim().toLowerCase() || ''

const loadProducts = () => {
  const { onSuccess, onError } = getAllProducts()

  onSuccess((products: any[]) => {
    const uniqueProducts = products.reduce((acc: any[], product) => {
      if (!acc.find((p) => p.name === product.name)) {
        acc.push(product)
      }

      return acc
    }, [])

    productOptions.value = uniqueProducts.map((product) => ({
      name: product.name,
      id: product.id,
    }))
  })

  onError((error) => {
    console.error('Error loading products:', error)
  })
}

const loadServices = () => {
  const { onSuccess, onError } = getAllServices()

  onSuccess((services: any[]) => {
    serviceOptions.value = services.map((service) => ({
      name: service.name,
      id: service.id,
    }))
  })

  onError((error) => {
    console.error('Error loading services:', error)
  })
}

const loadJiraProjects = () => {
  isLoadingJiraProjects.value = true

  const { onSuccess, onError } = fetchJiraProjects()

  onSuccess((projects: any[]) => {
    jiraProjects.value = projects.map((project) => ({
      id: project.id,
      name: `${project.name} (${project.key})`,
      label: `${project.name} (${project.key})`,
    }))
    isLoadingJiraProjects.value = false
  })

  onError((error: any) => {
    console.error('Error loading Jira projects:', error)
    isLoadingJiraProjects.value = false
  })
}

const loadTogglProjects = () => {
  if (!togglToken.value) {
    return
  }

  isLoadingTogglProjects.value = true

  const { onSuccess: onWorkspacesSuccess, onError: onWorkspacesError } = getTogglWorkspaces(togglToken.value)

  onWorkspacesSuccess((workspaces: any[]) => {
    if (workspaces.length === 0) {
      isLoadingTogglProjects.value = false

      return
    }

    const workspaceId = workspaces.length === 1 ? workspaces[0].id : workspaces[0].id

    togglWorkspaceId.value = workspaceId

    const { onSuccess: onProjectsSuccess, onError: onProjectsError } = getTogglProjects(togglToken.value, workspaceId)

    onProjectsSuccess((projects: any[]) => {
      togglProjects.value = projects
        .filter((project) => project.active === true)
        .map((project) => ({
          id: project.id,
          name: project.name,
          label: project.name,
          workspaceId,
        }))
      isLoadingTogglProjects.value = false
    })

    onProjectsError((error: any) => {
      console.error('Error loading Toggl projects:', error)
      isLoadingTogglProjects.value = false
    })
  })

  onWorkspacesError((error: any) => {
    console.error('Error loading Toggl workspaces:', error)
    isLoadingTogglProjects.value = false
  })
}

onMounted(() => {
  loadProducts()
  loadServices()
  loadJiraProjects()
  loadTogglProjects()
})

const baseSchemaFields = {
  customer: z
    .string({
      required_error: 'Customer name is required.',
    })
    .min(1, { message: 'Customer name is required.' }),
  tier_industry: z
    .object({
      tier: z.string().nullable().optional(),
      industry: z.string().nullable().optional(),
    })
    .superRefine((val, ctx) => {
      if (!val.tier) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tier is required.',
        })
      }
      if (!val.industry) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Industry is required.',
        })
      }
    }),
  prefix: prefixSchema,
  address: addressSchema,
  phone: phoneSchema,
  services: z.array(z.any()).optional(),
  linkedTeams: z.array(z.any()).optional(),
  domains: domainsSchema,
  jiraProjectId: z
    .union([
      z.string(),
      z.object({
        id: z.string(),
        name: z.string().optional(),
      }),
    ])
    .nullable()
    .optional(),
  togglProjectId: z
    .union([
      z.number(),
      z.object({
        id: z.number(),
        name: z.string().optional(),
        workspaceId: z.number().optional(),
      }),
    ])
    .nullable()
    .optional(),
}

const assignedManagerOptional = z
  .object({
    id: z.string(),
    name: z.string().optional(),
  })
  .optional()

const assignedManagerRequired = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
  })
  .nullable()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || !val.id || val.id === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Assigned Manager is required',
        path: ['id'],
      })
    }
  })

const schemaWithProducts = z.object({
  ...baseSchemaFields,
  products: z.array(z.any()).optional(),
  assigned_manager: assignedManagerOptional,
})

const schemaWithoutProducts = z.object({
  ...baseSchemaFields,
  assigned_manager: assignedManagerRequired,
})

const schema = computed(() => {
  return isEditMode.value ? schemaWithoutProducts : schemaWithProducts
})

const fieldConfig = reactive({
  customer: {
    label: 'Customer Name',
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Customer Name',
    },
  },
  tier_industry: {
    component: TierIndustryGroup,
    inputProps: {
      class: 'w-full lg:w-full',
      options: {
        tiers: ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'],
        industries: ['Finance', 'Healthcare', 'Education', 'Technology', 'Retail', 'Manufacturing', 'Other'],
      },
    },
  },
  prefix: {
    label: 'Prefix',
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Prefix',
    },
  },
  address: {
    label: 'Address',
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Address',
    },
  },
  phone: {
    label: 'Phone',
    component: PhoneInput,
    inputProps: {
      class: 'w-full',
      autoDefaultCountry: false,
      defaultCountry: 'US',
      modelValue: computed(() => {
        if (props.customer?.phone && props.customer.phone.trim() !== '') {
          return normalizePhoneNumber(props.customer.phone)
        }

        return undefined
      }),
    },
  },
  products: {
    label: 'Projects',
    component: 'multiselect',
    inputProps: {
      showIcon: true,
      placeholder: 'Select Projects',
      insideText: 'Project',
      class: 'w-full lg:w-full',
      options: productOptions,
    },
  },
  services: {
    label: 'Services',
    component: 'multiselect',
    inputProps: {
      showIcon: true,
      placeholder: 'Select Services',
      insideText: 'Service',
      class: 'w-full lg:w-full',
      options: serviceOptions,
    },
  },
  linkedTeams: {
    label: 'Linked Teams',
    component: 'multiselect',
    inputProps: {
      showIcon: true,
      placeholder: 'Select Teams',
      insideText: 'Team',
      class: 'w-full lg:w-full',
      options: computed(() => teams.value),
    },
  },
  domains: {
    label: 'Domains',
    component: DomainTags,
    multiple: true,
    inputProps: {
      class: 'w-full',
    },
    duplicateDomains,
  },
  jiraProjectId: {
    label: 'Jira Project',
    component: 'autocomplete',
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Select Jira Project',
      options: computed(() => jiraProjects.value),
      disabled: computed(() => isLoadingJiraProjects.value || jiraProjects.value.length === 0),
    },
  },
  togglProjectId: {
    label: 'Toggl Project',
    component: 'autocomplete',
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Select Toggl Project',
      options: computed(() => togglProjects.value),
      disabled: computed(() => !togglToken.value || isLoadingTogglProjects.value || togglProjects.value.length === 0),
    },
  },
  assigned_manager: {
    label: 'Assigned Manager',
    component: AutoFormFieldAutocomplete,
    inputProps: {
      class: 'w-full lg:w-full',
      placeholder: 'Search for a manager (min. 2 characters)...',
      minChars: 2,
    },
  },
})

const normalizePhoneNumber = (phoneString: string): string => {
  if (!phoneString) return ''
  const trimmed = phoneString.trim()

  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`
}

const initializeFormData = () => {
  if (props.customer) {
    const phoneData =
      props.customer.phone && props.customer.phone.trim() !== ''
        ? {
            number: normalizePhoneNumber(props.customer.phone),
            formatted: normalizePhoneNumber(props.customer.phone),
            valid: true,
            possible: true,
          }
        : undefined

    const transformedServices = Array.isArray(props.customer.services)
      ? props.customer.services.map((item: any) => item.service || item)
      : []

    const transformedLinkedTeams = Array.isArray(props.customer.linkedTeams)
      ? props.customer.linkedTeams.map((item: any) => item.team || item)
      : []

    const baseData = {
      customer: props.customer.name || '',
      tier_industry: {
        tier: props.customer.tier || null,
        industry: props.customer.industry || null,
      },
      prefix: props.customer.prefix || '',
      address: props.customer.address || '',
      phone: phoneData,
      services: transformedServices,
      linkedTeams: transformedLinkedTeams,
      domains: Array.isArray(props.customer.domains) ? props.customer.domains : [],
      jiraProjectId: (() => {
        const jiraId = (props.customer as any).jiraProjectId

        if (!jiraId) return undefined

        const foundProject = jiraProjects.value.find((p) => p.id === jiraId)

        const result = {
          id: jiraId,
          name: foundProject?.name || jiraId,
          label: foundProject?.label || jiraId,
        }

        return result
      })(),
      togglProjectId: (props.customer as any).togglProjectId
        ? {
            id: (props.customer as any).togglProjectId,
            name:
              (props.customer as any).togglProjectName ||
              togglProjects.value.find((p) => p.id === (props.customer as any).togglProjectId)?.name ||
              `Project ${(props.customer as any).togglProjectId}`,
            label:
              (props.customer as any).togglProjectName ||
              togglProjects.value.find((p) => p.id === (props.customer as any).togglProjectId)?.label ||
              `Project ${(props.customer as any).togglProjectId}`,
            workspaceId: (props.customer as any).togglWorkspaceId || togglWorkspaceId.value,
          }
        : undefined,
      assigned_manager: props.customer.assignedManager
        ? {
            id: props.customer.assignedManager.id,
            name: props.customer.assignedManager.name,
          }
        : undefined,
    }

    if (!isEditMode.value) {
      return {
        ...baseData,
        products: Array.isArray(props.customer.products) ? props.customer.products : [],
      }
    }

    return baseData
  } else {
    return {
      domains: [],
      products: [],
      services: [],
      linkedTeams: [],
    }
  }
}

const form = useForm({
  validationSchema: computed(() => toTypedSchema(schema.value)),
  initialValues: initializeFormData(),
})

// --- Field-level Validation ---
const { values, setFieldValue, setFieldError } = form

watch(
  () => values.prefix,
  (currentValue) => {
    if (currentValue && typeof currentValue === 'string') {
      const uppercaseValue = currentValue.toUpperCase()

      if (currentValue !== uppercaseValue) {
        setFieldValue('prefix', uppercaseValue)
      }
    }
  },
)

watch(jiraProjects, (newProjects) => {
  if (newProjects.length > 0 && values.jiraProjectId) {
    const currentValue = values.jiraProjectId

    if (typeof currentValue === 'object' && currentValue.id && currentValue.name === currentValue.id) {
      const foundProject = newProjects.find((p) => p.id === currentValue.id)

      if (foundProject) {
        const updatedValue = {
          id: foundProject.id,
          name: foundProject.name,
          label: foundProject.label,
        }

        setFieldValue('jiraProjectId', updatedValue)
      }
    }
  }
})

watch(togglProjects, (newProjects) => {
  if (newProjects.length > 0 && values.togglProjectId) {
    const currentValue = values.togglProjectId

    if (typeof currentValue === 'object' && currentValue.id && !currentValue.label) {
      const foundProject = newProjects.find((p) => p.id === currentValue.id)

      if (foundProject) {
        setFieldValue('togglProjectId', {
          id: foundProject.id,
          name: foundProject.name,
          label: foundProject.label,
          workspaceId: foundProject.workspaceId,
        })
      }
    }
  }
})

const isFormDisabled = computed(() => {
  return isLoading.value
})

const onSubmit = async (values: Record<string, any>) => {
  if (!isEditMode.value && !confirmDuplicate.value) {
    const newName = normalize(values.customer)
    const duplicateExists = companyStore.companies.some((c) => normalize(c.name) === newName && c.id !== props.customer?.id)

    if (duplicateExists) {
      pendingValues.value = values
      showDuplicateDialog.value = true

      return
    }
  }
  isLoading.value = true
  backendError.value = ''
  form.setErrors({})

  duplicateDomains.value = []
  let handled = false

  const customerData = {
    name: values.customer,
    description: ' ',
    prefix: values.prefix,
    address: values.address,
    domains: values.domains,
    phone: values.phone?.number ? values.phone.number : '',
    tier: values.tier_industry?.tier || '',
    industry: values.tier_industry?.industry || '',
    products: values.products?.map((product: any) => product.id || product) || [],
    services: values.services?.map((service: any) => service.id || service) || [],
    linkedTeams: values.linkedTeams?.map((team: any) => team.id || team) || [],
    assignedManagerId: values.assigned_manager?.id || null,
    jiraProjectId: values.jiraProjectId?.id || values.jiraProjectId || null,
    togglProjectId: values.togglProjectId?.id || values.togglProjectId || null,
    togglProjectName: values.togglProjectId?.name || null,
    togglWorkspaceId: values.togglProjectId?.workspaceId || togglWorkspaceId.value || null,
  }

  const { onSuccess, onError } =
    isEditMode.value && props.customer ? updateCompanies(props.customer.id, customerData) : createCompanies(customerData)

  onSuccess(async (data: any) => {
    const customer: Company = {
      id: data.id,
      name: data.name,
      description: data.description,
      address: data.address,
      prefix: data.prefix,
      phone: data.phone || '',
      mainContact: data.mainContact ?? {
        id: '',
        name: '',
        email: '',
        phone: '',
        avatar: '',
        customData: {},
        identities: {},
        lastSignInAt: BigInt(0),
        createdAt: BigInt(0),
        updatedAt: BigInt(0),
        applicationId: '',
        isSuspended: false,
        hasPassword: false,
        profile: {},
      },
      mainContactId: data.mainContactId,
      domains: data.domains ?? [],
      meta: data.meta ?? { totalMembers: 0, totalTickets: 0 },
      branding: data.branding,
      tier: data.tier,
      industry: data.industry,
      products: data.products || [],
      services: data.services?.map((item: any) => item.service || item) || [],
      linkedTeams: data.linkedTeams?.map((item: any) => item.team || item) || [],
      integrations: data.integrations || [],
      assignedManagerId: data.assignedManagerId,
      assignedManager: data.assignedManager,
      jiraProjectId: data.jiraProjectId,
      togglProjectId: data.togglProjectId,
      togglProjectName: data.togglProjectName,
      togglWorkspaceId: data.togglWorkspaceId,
    }

    if (isEditMode.value) {
      const index = companyStore.companies.findIndex((c) => c.id === customer.id)

      if (index !== -1) {
        companyStore.companies[index] = customer
      }
    } else {
      companyStore.companies.unshift(customer)
    }

    await nextTick()
    emit('closeDialog')
    isLoading.value = false
    handled = true
  })
  onError((err: any) => {
    isLoading.value = false
    handled = true
    let specificErrorHandled = false

    const errorMessageString = err?.message

    if (errorMessageString && typeof errorMessageString === 'string') {
      const msg = errorMessageString.toLowerCase()

      if (
        msg.includes('prefix') &&
        (msg.includes('unique') || msg.includes('already exist') || msg.includes('already in use'))
      ) {
        setFieldError('prefix', errorMessageString)
        specificErrorHandled = true
      } else if (
        msg.includes('domain') &&
        (msg.includes('unique') ||
          msg.includes('already exist') ||
          msg.includes('already in use') ||
          msg.includes('registered'))
      ) {
        let specificDomainMsg = errorMessageString
        const domainMatch = errorMessageString.match(
          /domain(?:s)? (?:is|are|is already|are already) (?:in use|registered):? (.+?)(?:\.|is already in use|is already registered)?$/i,
        )

        if (domainMatch && domainMatch[1]) {
          const problematicDomains = domainMatch[1]
            .split(',')
            .map((d: string) => d.trim())
            .join(', ')

          specificDomainMsg = `The following domain(s) are already in use: ${problematicDomains}. Please choose others.`
          duplicateDomains.value = domainMatch[1].split(',').map((d: string) => d.trim())
        } else {
          const singleDomainMatch = errorMessageString.match(/domain '([^']*)'/i)

          if (singleDomainMatch && singleDomainMatch[1]) {
            duplicateDomains.value = [singleDomainMatch[1]]
          }
        }
        setFieldError('domains', specificDomainMsg)
        specificErrorHandled = true
      }
    }

    if (!specificErrorHandled) {
      if (errorMessageString && typeof errorMessageString === 'string') {
        backendError.value = errorMessageString
      } else {
        backendError.value = 'An unknown error occurred. Please try again.'
      }
    } else {
      backendError.value = ''
    }

    if (backendError.value && !specificErrorHandled) {
      const currentBackendErrorMessage = backendError.value

      setTimeout(() => {
        if (backendError.value === currentBackendErrorMessage) {
          backendError.value = ''
        }
      }, 6000)
    }
  })
  setTimeout(() => {
    if (!handled) isLoading.value = false
  }, 5000)
}

function handleConfirmDuplicate() {
  confirmDuplicate.value = true
  showDuplicateDialog.value = false
  if (pendingValues.value) {
    onSubmit(pendingValues.value)
    pendingValues.value = null
  }
}

function handleCancelDuplicate() {
  showDuplicateDialog.value = false
  pendingValues.value = null
  isSubmitting.value = false
}

const errorMessage = computed(() => {
  if (!isSubmitting.value) {
    return ''
  }
  if (form.errors.value && Object.keys(form.errors.value).length > 0) {
    const errors = Object.values(form.errors.value)
    const requiredErrors = errors.filter((error) => typeof error === 'string' && error.includes('required'))

    if (requiredErrors.length > 0) {
      return 'Please fill in all the required fields to create the customer'
    }

    return typeof errors[0] === 'string' ? errors[0] : ''
  }

  return ''
})

// Filter fieldConfig to hide products in edit mode
const visibleFieldConfig = computed(() => {
  if (isEditMode.value) {
    const { products, ...rest } = fieldConfig

    return rest
  }

  return fieldConfig
})

onMounted(() => {})
</script>

<template>
  <div class="flex flex-col w-full" :style="{ height: 'calc(100% - 70px)' }">
    <Alert v-if="backendError" variant="destructive" class="!bg-destructive mb-5 !flex items-center text-white">
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold whitespace-pre-line">
        {{ backendError }}
      </AlertDescription>
    </Alert>
    <Alert
      v-if="errorMessage && !backendError"
      variant="destructive"
      class="!bg-destructive mb-5 !flex items-center text-white"
    >
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold">
        {{ errorMessage }}
      </AlertDescription>
    </Alert>

    <div class="flex w-full" :class="{ 'pb-20': isEditMode }">
      <AutoForm
        class="w-full space-y-5"
        :schema="schema"
        :field-config="visibleFieldConfig as any"
        :form="form"
        @submit="onSubmit"
      >
        <!-- Custom slot for Toggl Project field -->
        <template #togglProjectId="{ config, fieldName, shape }">
          <AutoFormField :config="config" :field-name="fieldName" :shape="shape" />
          <div v-if="!togglToken" class="mt-2 p-3 bg-muted/30 border border-border rounded-lg">
            <div class="flex items-start gap-2.5">
              <img src="@/assets/toggl.png" alt="Toggl" class="h-4 w-4 mt-0.5 opacity-50 flex-shrink-0" />
              <div class="flex-1">
                <p class="text-xs text-muted-foreground mb-2">Configure your Toggl API token to link projects</p>
                <Button variant="outline" size="sm" class="text-xs h-7" @click="router.push('/settings/team/users')">
                  <ExternalLink :size="12" class="mr-1.5" />
                  Configure Token
                </Button>
              </div>
            </div>
          </div>
        </template>

        <div>
          <div
            class="flex justify-end w-full gap-2 absolute bottom-0 p-4 rounded-b-sm right-0 border-t-2 border-border z-[100] bg-create-form-footer"
          >
            <DialogCheck :show-icon="true" @dialog-should-close="$emit('closeDialog')" />
            <slot v-bind="{ onSubmit }">
              <Button type="submit" :disabled="isFormDisabled" @click="isSubmitting = true">
                <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" />
                {{ isEditMode ? 'Update' : 'Create' }}
              </Button>
            </slot>
          </div>
        </div>
      </AutoForm>
    </div>

    <!-- Duplicate confirmation dialog -->
    <Dialog v-model:open="showDuplicateDialog">
      <DialogTrigger class="hidden" />
      <DialogContent
        class="!w-[90vw] !max-w-[500px] !md:max-w-[500px] !lg:max-w-[500px] max-h-[85vh] p-[25px] pb-4 rounded-[6px]"
      >
        <DialogHeader>
          <DialogTitle>Duplicate Customer Name</DialogTitle>
          <DialogDescription class="my-5">
            The system already contains a customer with this name.
            <span class="font-semibold">
              Would you like to continue and {{ isEditMode ? 'update' : 'create a new one' }}?
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="handleCancelDuplicate">Cancel</Button>
          <Button @click="handleConfirmDuplicate">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
